import React, { useMemo, useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import ScoreGauge from "../../Components/Charts/ScoreGauge";
import { chipClass } from "./shared";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  BellIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CloudIcon,
  CloudUploadIcon,
  DatabaseIcon,
  DownloadIcon,
  EyeIcon,
  FileIcon,
  FilterIcon,
  FolderIcon,
  GridIcon,
  HistoryIcon,
  HomeIcon,
  InfoIcon,
  LightbulbIcon,
  MoreIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  SheetIcon,
  ShieldCheckIcon,
  SparkleIcon,
  SyncIcon,
  UserIcon,
  XCircleIcon,
  XIcon,
} from "../../Components/Icons";
import {
  IMPORT_JOBS,
  IMPORT_METHODS,
  IMPORT_STEPS,
  IMPORT_SUMMARY,
  PREVIEW_BREAKDOWN,
  PREVIEW_TOTAL,
  UPLOADED_FILE,
} from "./data";
import {
  FIELD_MAPPINGS,
  IMPORT_RESULT,
  IMPORT_SOURCES,
  MAPPING_PREVIEW,
  MAPPING_STATS,
  MAPPING_TIPS,
  NEXT_STEPS,
  QUALITY_SCORE,
  RESULT_SUMMARY,
  RESULT_TILES,
  SOURCE_COLUMNS,
  SYSTEM_FIELDS,
  TOP_ISSUES,
  VALIDATION_BREAKDOWN,
  VALIDATION_ROWS,
  VALIDATION_RULES,
  VALIDATION_SUMMARY,
  VALIDATION_TABS,
  VALIDATION_TOTAL,
  WHAT_NEXT,
} from "./importSteps";
import "./DataIntegration.css";
import "./ImportSteps.css";

const METHOD_ICONS = {
  cloudUpload: CloudUploadIcon,
  database: DatabaseIcon,
  cloud: CloudIcon,
  folder: FolderIcon,
  file: FileIcon,
  settings: SettingsIcon,
};

const SUMMARY_ICONS = {
  database: DatabaseIcon,
  sheet: SheetIcon,
  file: FileIcon,
  grid: GridIcon,
  user: UserIcon,
  clock: ClockIcon,
  shield: ShieldCheckIcon,
  check: CheckCircleIcon,
  error: XCircleIcon,
  warn: AlertTriangleIcon,
  info: AlertCircleIcon,
  bell: BellIcon,
  home: HomeIcon,
};

const ROW_STATUS_ICONS = {
  Valid: CheckCircleIcon,
  Error: XCircleIcon,
  Warning: AlertTriangleIcon,
};

const ISSUE_ICONS = {
  error: XCircleIcon,
  warning: AlertTriangleIcon,
  info: AlertCircleIcon,
};

/** Key/value list used by every step's sidebar summary. */
function SummaryList({ rows }) {
  return (
    <ul className="isummary">
      {rows.map((row) => {
        const Icon = SUMMARY_ICONS[row.icon] ?? FileIcon;
        return (
          <li key={row.label}>
            <span
              className={`isummary__icon${
                row.tone ? ` isummary__icon--${row.tone}` : ""
              }`}
            >
              <Icon size={16} />
            </span>
            <span className="isummary__label">{row.label}</span>
            <span
              className={`isummary__value${
                row.tone ? ` isummary__value--${row.tone}` : ""
              }`}
            >
              {row.value}
              {row.chip && <span className={chipClass(row.chip)}>{row.chip}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default function ImportData() {
  const [step, setStep] = useState(0);
  const [method, setMethod] = useState("file");
  const [source, setSource] = useState("database");
  const [file, setFile] = useState(UPLOADED_FILE);
  const [mappings, setMappings] = useState(FIELD_MAPPINGS);
  const [validationTab, setValidationTab] = useState("all");
  const [dataSearch, setDataSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const goTo = (index) => {
    setStep(Math.min(Math.max(index, 0), IMPORT_STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const visibleMapped = mappings.filter((row) => row.target).length;
  const baselineMapped = FIELD_MAPPINGS.filter((row) => row.target).length;
  // Only 12 of the 28 columns are listed; report the file totals shifted by
  // whatever the user changed on this page.
  const mappedCount = MAPPING_STATS.mapped + (visibleMapped - baselineMapped);
  const unmappedCount = MAPPING_STATS.totalColumns - mappedCount;
  const mappedShare = Math.round((mappedCount / MAPPING_STATS.totalColumns) * 100);
  const unmappedShare = 100 - mappedShare;

  const setTarget = (index) => (event) => {
    const { value } = event.target;
    setMappings((prev) =>
      prev.map((row, i) => (i === index ? { ...row, target: value } : row))
    );
  };

  const autoMap = () =>
    setMappings(
      mappings.map((row) => {
        if (row.target) return row;
        // Match on a normalised name; anything without a match stays unmapped.
        const guess = SYSTEM_FIELDS.find((field) =>
          field.toLowerCase().includes(row.source.toLowerCase())
        );
        return guess ? { ...row, target: guess } : row;
      })
    );

  const validationRows = useMemo(() => {
    const term = dataSearch.trim().toLowerCase();
    return VALIDATION_ROWS.filter((row) => {
      if (validationTab === "Duplicate" && !row.issue.includes("Duplicate")) {
        return false;
      }
      if (
        validationTab !== "all" &&
        validationTab !== "Duplicate" &&
        row.status !== validationTab
      ) {
        return false;
      }
      if (term && !`${row.nif} ${row.name} ${row.email}`.toLowerCase().includes(term)) {
        return false;
      }
      return true;
    });
  }, [validationTab, dataSearch]);

  /* --------------------------------------------------- 1. upload file -- */

  const renderUpload = () => (
    <section className="dgi-card">
      <h2 className="wiz__title">1. Choose Import Method</h2>
      <p className="wiz__lede">
        Select how you want to import data into the system.
      </p>

      <div className="row g-3 wiz__types">
        {IMPORT_METHODS.map((item) => {
          const Icon = METHOD_ICONS[item.icon];
          const active = method === item.key;
          return (
            <div className="col-6 col-xl-3" key={item.key}>
              <button
                type="button"
                className={`type-card${active ? " type-card--active" : ""}`}
                aria-pressed={active}
                onClick={() => setMethod(item.key)}
              >
                {active && (
                  <span className="type-card__check">
                    <CheckCircleIcon size={20} />
                  </span>
                )}
                <span className={`type-card__icon kpi__icon--${item.tone}`}>
                  <Icon size={28} />
                </span>
                <span className="type-card__label">{item.label}</span>
                <span className="type-card__text">{item.text}</span>
              </button>
            </div>
          );
        })}
      </div>

      <h2 className="wiz__title wiz__title--sm di-upload__title">2. Upload File</h2>
      <p className="wiz__lede">
        Choose a file to import. Supported formats: CSV, XLSX, XLS, XML
      </p>

      <div className="dropzone">
        <CloudUploadIcon size={28} />
        <p className="dropzone__text">Drag and drop your file here</p>
        <p className="dropzone__or">or</p>
        <button type="button" className="dgi-btn dgi-btn--primary">
          Browse Files
        </button>
      </div>

      {file && (
        <div className="upload">
          <span className="upload__icon">
            <SheetIcon size={20} />
          </span>
          <span className="upload__meta">
            <span className="upload__name">{file.name}</span>
            <span className="upload__sub">
              {file.size} • {file.rows}
            </span>
          </span>
          <span className="upload__ok">
            <CheckCircleIcon size={15} />
            File uploaded successfully
          </span>
          <button
            type="button"
            className="upload__remove"
            aria-label="Remove file"
            onClick={() => setFile(null)}
          >
            <XIcon size={16} />
          </button>
        </div>
      )}

      <div className="wiz__foot">
        <button type="button" className="link di-template">
          <DownloadIcon size={16} />
          Download template
        </button>
        <button
          type="button"
          className="dgi-btn dgi-btn--primary"
          disabled={!file}
          onClick={() => goTo(1)}
        >
          Next <ArrowRightIcon size={16} />
        </button>
      </div>
    </section>
  );

  /* -------------------------------------------------- 2. choose source -- */

  const renderSource = () => (
    <section className="dgi-card">
      <h2 className="wiz__title">2. Choose Source</h2>
      <p className="wiz__lede">Select the source type for the uploaded data.</p>

      <div className="row g-3 wiz__types">
        {IMPORT_SOURCES.map((item) => {
          const Icon = METHOD_ICONS[item.icon];
          const active = source === item.key;
          return (
            <div className="col-12 col-sm-6 col-xl-4" key={item.key}>
              <button
                type="button"
                className={`type-card${active ? " type-card--active" : ""}`}
                aria-pressed={active}
                onClick={() => setSource(item.key)}
              >
                {active && (
                  <span className="type-card__check">
                    <CheckCircleIcon size={20} />
                  </span>
                )}
                <span className={`type-card__icon kpi__icon--${item.tone}`}>
                  <Icon size={28} />
                </span>
                <span className="type-card__label">{item.label}</span>
                <span className="type-card__text">{item.text}</span>
              </button>
            </div>
          );
        })}
      </div>

      <p className="di-note">
        <InfoIcon size={17} />
        Choose the source that best matches where your data is coming from.
      </p>

      {renderNav(1)}
    </section>
  );

  /* ------------------------------------------------------ 3. map data -- */

  const renderMap = () => (
    <section className="dgi-card">
      <div className="wiz__review-head">
        <div>
          <h2 className="wiz__title">3. Map Data</h2>
          <p className="wiz__lede">
            Map the columns from your source to the DGI Smart Tax Registry
            fields.
          </p>
        </div>
        <div className="map__actions">
          <button
            type="button"
            className="dgi-btn dgi-btn--ghost dgi-btn--sm"
            onClick={autoMap}
          >
            <SparkleIcon size={16} />
            Auto Map
          </button>
          <button
            type="button"
            className="dgi-btn dgi-btn--ghost dgi-btn--sm"
            onClick={() => setMappings(FIELD_MAPPINGS)}
          >
            <SyncIcon size={16} />
            Reset Mapping
          </button>
        </div>
      </div>

      <div className="map__strip">
        <span className="map__strip-item">
          <span className="upload__icon map__file-icon">
            <SheetIcon size={17} />
          </span>
          <span>
            <span className="map__strip-label">Source</span>
            <span className="map__strip-value">{file?.name ?? "—"}</span>
          </span>
        </span>
        <span className="map__strip-item">
          <span>
            <span className="map__strip-label">Total Columns</span>
            <span className="map__strip-value">{MAPPING_STATS.totalColumns}</span>
          </span>
        </span>
        <span className="map__strip-item">
          <span>
            <span className="map__strip-label">Mapped</span>
            <span className="map__strip-value map__strip-value--good">
              {mappedCount} ({mappedShare}%)
            </span>
          </span>
        </span>
        <span className="map__strip-item">
          <span>
            <span className="map__strip-label">Unmapped</span>
            <span className="map__strip-value map__strip-value--warn">
              {unmappedCount} ({unmappedShare}%)
            </span>
          </span>
        </span>
      </div>

      <div className="map__head">
        <span>Source Column (File)</span>
        <span>DGI System Field</span>
        <span>Mapping Status</span>
      </div>

      <ul className="map__rows">
        {mappings.map((row, index) => (
          <li className="map__row" key={row.source}>
            <span className="select select--block">
              <select value={row.source} onChange={() => {}} aria-label="Source column">
                {SOURCE_COLUMNS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>

            <span className="select select--block">
              <select
                value={row.target}
                onChange={setTarget(index)}
                aria-label={`System field for ${row.source}`}
              >
                <option value="">Select a field</option>
                {SYSTEM_FIELDS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>

            <span
              className={`map__status map__status--${
                row.target ? "mapped" : "unmapped"
              }`}
            >
              {row.target ? (
                <CheckCircleIcon size={16} />
              ) : (
                <AlertTriangleIcon size={16} />
              )}
              {row.target ? "Mapped" : "Unmapped"}
            </span>
          </li>
        ))}
      </ul>

      <p className="listbar__count map__count">
        Showing 1 to {mappings.length} of {MAPPING_STATS.totalColumns} columns
      </p>

      {renderNav(2)}
    </section>
  );

  /* ------------------------------------------------- 4. validate data -- */

  const renderValidate = () => (
    <section className="dgi-card">
      <div className="wiz__review-head">
        <div>
          <h2 className="wiz__title">4. Validate Data</h2>
          <p className="wiz__lede">
            Review the validation results and fix issues before importing.
          </p>
        </div>
        <div className="map__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
            <SyncIcon size={16} />
            Re-validate
          </button>
          <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
            <DownloadIcon size={16} />
            Download Report
          </button>
        </div>
      </div>

      <div className="validate__panels">
        <article className="validate__panel">
          <h3 className="validate__title">Validation Summary</h3>
          <DonutChart
            segments={VALIDATION_BREAKDOWN}
            total={VALIDATION_TOTAL}
            totalLabel="Total Rows"
          />
        </article>

        <article className="validate__panel validate__panel--score">
          <h3 className="validate__title">Data Quality Score</h3>
          <ScoreGauge
            value={QUALITY_SCORE}
            display={`${QUALITY_SCORE}%`}
            label="Good"
            size="sm"
          />
          <p className="validate__note">
            The data quality is good. You can proceed to import.
          </p>
        </article>

        <article className="validate__panel">
          <h3 className="validate__title">Top Issues</h3>
          <ul className="issues">
            {TOP_ISSUES.map((issue) => {
              const Icon = ISSUE_ICONS[issue.tone];
              return (
                <li key={issue.label}>
                  <span className={`issues__icon issues__icon--${issue.tone}`}>
                    <Icon size={16} />
                  </span>
                  <span className="issues__label">{issue.label}</span>
                  <span className="issues__count">{issue.count}</span>
                </li>
              );
            })}
          </ul>
        </article>
      </div>

      <div className="tabs tabs--scroll" role="tablist">
        {VALIDATION_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={validationTab === tab.key}
            className={`tabs__tab${
              validationTab === tab.key ? " tabs__tab--active" : ""
            }`}
            onClick={() => setValidationTab(tab.key)}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <div className="validate__toolbar">
        <div className="filters__search">
          <input
            type="search"
            placeholder="Search in data..."
            aria-label="Search in data"
            value={dataSearch}
            onChange={(event) => setDataSearch(event.target.value)}
          />
          <SearchIcon size={16} />
        </div>
        <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
          <FilterIcon size={16} />
          Filter
        </button>
        <span className="validate__rows">
          Show
          <span className="select">
            <select
              value={rowsPerPage}
              aria-label="Rows per page"
              onChange={(event) => setRowsPerPage(Number(event.target.value))}
            >
              {[25, 50, 100].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDownIcon size={16} />
          </span>
          rows per page
        </span>
      </div>

      <div className="dgi-table-wrap">
        <table className="dgi-table dgi-table--validate">
          <thead>
            <tr>
              <th>NIF</th>
              <th>Taxpayer Name</th>
              <th>Entity Type</th>
              <th>Registration Date</th>
              <th>Email</th>
              <th>Status</th>
              <th>Issue</th>
            </tr>
          </thead>
          <tbody>
            {validationRows.map((row) => {
              const Icon = ROW_STATUS_ICONS[row.status];
              return (
                <tr key={row.id}>
                  <td className={row.badNif ? "cell--bad" : undefined}>
                    {row.nif}
                  </td>
                  <td>{row.name}</td>
                  <td>{row.type}</td>
                  <td>{row.registered}</td>
                  <td className={row.badEmail ? "cell--bad" : undefined}>
                    {row.email}
                  </td>
                  <td>
                    <span
                      className={`rowstat rowstat--${row.status.toLowerCase()}`}
                    >
                      <Icon size={15} />
                      {row.status}
                    </span>
                  </td>
                  <td>{row.issue}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {validationRows.length === 0 && (
        <p className="dgi-table__empty">No rows in this category.</p>
      )}

      <div className="listbar listbar--foot">
        <p className="listbar__count">
          Showing 1 to {validationRows.length} of {VALIDATION_TOTAL} rows
        </p>
        <nav className="pager" aria-label="Pagination">
          <button type="button" className="pager__btn" disabled aria-label="Previous page">
            <ChevronLeftIcon size={16} />
          </button>
          {[1, 2, 3].map((entry) => (
            <button
              key={entry}
              type="button"
              className={`pager__btn${entry === 1 ? " pager__btn--active" : ""}`}
              aria-current={entry === 1 ? "page" : undefined}
            >
              {entry}
            </button>
          ))}
          <span className="pager__gap">…</span>
          <button type="button" className="pager__btn">
            9828
          </button>
          <button type="button" className="pager__btn" aria-label="Next page">
            <ChevronRightIcon size={16} />
          </button>
        </nav>
      </div>

      {renderNav(3)}
    </section>
  );

  /* ------------------------------------------------ 5. import complete -- */

  const renderComplete = () => (
    <section className="dgi-card">
      <div className="wiz__review-head">
        <div>
          <h2 className="wiz__title">5. Import &amp; Complete</h2>
          <p className="wiz__lede">Your data has been imported successfully.</p>
        </div>
        <div className="map__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
            <FileIcon size={16} />
            View Import Log
          </button>
          <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
            <DownloadIcon size={16} />
            Download Summary
          </button>
        </div>
      </div>

      <div className="done">
        <span className="done__badge">
          <CheckCircleIcon size={64} />
        </span>
        <div className="done__body">
          <h3 className="done__title">Import Completed Successfully!</h3>
          <p className="done__text">
            Your data has been imported and is now available in the DGI Smart
            Tax Registry.
          </p>
          <dl className="done__meta">
            {IMPORT_RESULT.map((row) => (
              <div key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <h3 className="wiz__group">Import Results Overview</h3>
      <div className="row g-3">
        {RESULT_TILES.map((tile) => {
          const Icon = SUMMARY_ICONS[tile.icon];
          return (
            <div className="col-6 col-xl" key={tile.key}>
              <article className={`rtile rtile--${tile.tone}`}>
                <span className="rtile__icon">
                  <Icon size={20} />
                </span>
                <p className="rtile__value">{tile.value}</p>
                <p className="rtile__label">{tile.label}</p>
                {tile.share && <p className="rtile__share">{tile.share}</p>}
              </article>
            </div>
          );
        })}
      </div>

      <div className="next-panel">
        <h3 className="next-panel__title">What happens next?</h3>
        <ul className="next-panel__list">
          {WHAT_NEXT.map((item) => {
            const Icon = SUMMARY_ICONS[item.icon];
            return (
              <li key={item.key}>
                <span className="next-panel__icon">
                  <Icon size={18} />
                </span>
                <span>
                  <span className="next-panel__label">{item.title}</span>
                  <span className="next-panel__text">{item.text}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="wiz__foot">
        <button
          type="button"
          className="dgi-btn dgi-btn--ghost"
          onClick={() => goTo(step - 1)}
        >
          <ArrowLeftIcon size={16} />
          Previous
        </button>
        <button type="button" className="dgi-btn dgi-btn--primary">
          Go to Dashboard <ArrowRightIcon size={16} />
        </button>
      </div>
    </section>
  );

  /* ------------------------------------------------------------ chrome -- */

  function renderNav(current) {
    return (
      <div className="wiz__foot">
        <button
          type="button"
          className="dgi-btn dgi-btn--ghost"
          onClick={() => goTo(current - 1)}
        >
          <ArrowLeftIcon size={16} />
          Previous
        </button>
        <button
          type="button"
          className="dgi-btn dgi-btn--primary"
          onClick={() => goTo(current + 1)}
        >
          Next <ArrowRightIcon size={16} />
        </button>
      </div>
    );
  }

  const STEP_VIEWS = [
    renderUpload,
    renderSource,
    renderMap,
    renderValidate,
    renderComplete,
  ];

  /* Sidebar content differs per step. */
  const sidebarSummary = [
    IMPORT_SUMMARY,
    IMPORT_SUMMARY,
    [
      ...IMPORT_SUMMARY.slice(0, 4),
      {
        label: "Mapped Fields",
        value: `${mappedCount} (${mappedShare}%)`,
        icon: "user",
      },
      {
        label: "Unmapped Fields",
        value: `${unmappedCount} (${unmappedShare}%)`,
        icon: "info",
      },
    ],
    VALIDATION_SUMMARY,
    RESULT_SUMMARY,
  ][step];

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Import Data</h1>
          <p className="page__subtitle">
            Import data from files or external systems into DGI Smart Tax
            Registry.
          </p>
        </div>
        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <HistoryIcon size={17} />
            Import History
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <PlusIcon size={17} />
            New Import
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------ stepper -- */}
      <nav className="dgi-card stepper" aria-label="Import steps">
        {IMPORT_STEPS.map((item, index) => {
          const done = index < step;
          const current = index === step;
          return (
            <React.Fragment key={item.key}>
              {index > 0 && <span className="stepper__gap" aria-hidden="true" />}
              <button
                type="button"
                className={`stepper__step${
                  current ? " stepper__step--current" : ""
                }${done ? " stepper__step--done" : ""}`}
                onClick={() => index <= step && goTo(index)}
                disabled={index > step}
                aria-current={current ? "step" : undefined}
              >
                <span className="stepper__badge">
                  {done ? <CheckCircleIcon size={18} /> : index + 1}
                </span>
                <span className="stepper__meta">
                  <span className="stepper__title">{item.title}</span>
                  <span className="stepper__hint">{item.hint}</span>
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      <div className="wiz">
        <div className="stack">
          {STEP_VIEWS[step]()}

          {step === 0 && (
            <section className="dgi-card">
              <div className="dgi-card__header">
                <h2 className="dgi-card__title">Recent Import Jobs</h2>
              </div>
              <div className="dgi-table-wrap">
                <table className="dgi-table dgi-table--jobs">
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Source</th>
                      <th>File Name</th>
                      <th>Rows</th>
                      <th>Status</th>
                      <th>Started By</th>
                      <th>Start Time</th>
                      <th>Duration</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {IMPORT_JOBS.map((job) => (
                      <tr key={job.id}>
                        <td>{job.id}</td>
                        <td>{job.source}</td>
                        <td>{job.file}</td>
                        <td>{job.rows}</td>
                        <td>
                          <span className={chipClass(job.status)}>
                            {job.status}
                          </span>
                        </td>
                        <td>{job.startedBy}</td>
                        <td>{job.start}</td>
                        <td>{job.duration}</td>
                        <td>
                          <span className="row-actions">
                            <button
                              type="button"
                              className="row-actions__btn"
                              aria-label={`View ${job.id}`}
                            >
                              <EyeIcon size={16} />
                            </button>
                            <button
                              type="button"
                              className="dgi-card__more"
                              aria-label={`Actions for ${job.id}`}
                            >
                              <MoreIcon size={18} />
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="dgi-card__link">
                View all import history <ArrowRightIcon size={16} />
              </button>
            </section>
          )}
        </div>

        {/* -------------------------------------------------- sidebar -- */}
        <div className="stack">
          <section className="dgi-card">
            <h2 className="wiz__side-title">Import Summary</h2>
            <SummaryList rows={sidebarSummary} />
          </section>

          {step <= 1 && (
            <section className="dgi-card">
              <div className="dgi-card__header">
                <h2 className="wiz__side-title">Data Overview (Preview)</h2>
                <button
                  type="button"
                  className="dgi-card__link dgi-card__link--inline"
                >
                  View Full Preview <ArrowRightIcon size={16} />
                </button>
              </div>
              <DonutChart
                segments={PREVIEW_BREAKDOWN}
                total={PREVIEW_TOTAL}
                totalLabel="Total Rows"
              />
              <p className="di-note">
                <InfoIcon size={17} />
                Detailed validation results will be available in the next step.
              </p>
            </section>
          )}

          {step === 2 && (
            <>
              <section className="dgi-card tips">
                <h2 className="wiz__side-title tips__title">
                  <LightbulbIcon size={17} />
                  Mapping Tips
                </h2>
                <ul className="tips__list">
                  {MAPPING_TIPS.map((tip) => (
                    <li key={tip}>
                      <CheckCircleIcon size={15} />
                      {tip}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="dgi-card">
                <div className="dgi-card__header">
                  <h2 className="wiz__side-title tips__title">
                    <EyeIcon size={17} />
                    Preview (First 5 Rows)
                  </h2>
                  <button
                    type="button"
                    className="dgi-btn dgi-btn--ghost dgi-btn--sm"
                  >
                    View Preview
                  </button>
                </div>
                <div className="dgi-table-wrap">
                  <table className="dgi-table dgi-table--compact">
                    <thead>
                      <tr>
                        <th>NIF</th>
                        <th>Taxpayer Name</th>
                        <th>Entity Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MAPPING_PREVIEW.map((row) => (
                        <tr key={row.nif + row.name}>
                          <td>{row.nif}</td>
                          <td>{row.name}</td>
                          <td>{row.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {step === 3 && (
            <>
              <section className="dgi-card">
                <h2 className="wiz__side-title">Validation Rules</h2>
                <ul className="rules">
                  {VALIDATION_RULES.map((rule) => (
                    <li key={rule.text} className={`rules--${rule.tone}`}>
                      {rule.tone === "ok" ? (
                        <CheckCircleIcon size={15} />
                      ) : (
                        <AlertTriangleIcon size={15} />
                      )}
                      {rule.text}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="dgi-card nextstep">
                <h2 className="wiz__side-title tips__title">
                  <InfoIcon size={17} />
                  Next Step
                </h2>
                <p className="nextstep__text">
                  If the validation looks good, you can proceed to import the
                  data into the system.
                </p>
                <button
                  type="button"
                  className="dgi-btn dgi-btn--primary nextstep__btn"
                  onClick={() => goTo(4)}
                >
                  Proceed to Import <ArrowRightIcon size={16} />
                </button>
                <p className="nextstep__note">
                  <ShieldCheckIcon size={14} />
                  No data will be imported until you confirm in the next step.
                </p>
              </section>
            </>
          )}

          {step === 4 && (
            <>
              <section className="dgi-card alldone">
                <span className="alldone__icon">
                  <CheckCircleIcon size={26} />
                </span>
                <div>
                  <p className="alldone__title">All done!</p>
                  <p className="alldone__text">
                    Your import has been completed successfully. You can now
                    explore your data in the system.
                  </p>
                </div>
              </section>

              <section className="dgi-card">
                <h2 className="wiz__side-title">Next Steps</h2>
                <ul className="nextlinks">
                  {NEXT_STEPS.map((item) => {
                    const Icon = SUMMARY_ICONS[item.icon];
                    return (
                      <li key={item.key}>
                        <button type="button" className="nextlink">
                          <Icon size={16} />
                          <span>{item.label}</span>
                          <ArrowRightIcon size={15} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
